// ─── Structural edits to a journey tree ───────────────────────────────────────
// Pure, no React — imported by the editor and by test/journeyEdit.
//
// Adding a step to the end of a branch was always possible. GATING what already
// follows was not, and that is the edit people actually need: a branch that says
//
//   ✉ "now take the last step"      (right away)
//   ✉ "final reminder"              (+2 days)
//
// sends the final reminder to EVERYONE on that branch — including anyone who
// registered during those two days, because the engine only re-checks signals at
// a DECISION, never between two message steps. The fix is to put a decision
// between them, with the trailing steps moved onto its "didn't" arm. Rebuilding
// the journey to do that loses the live one; this does it in place.
//
// Ids are never reused or renumbered: new nodes take numbers above the current
// maximum, so anyone parked mid-journey keeps their node_id and is unaffected.

// One shared counter across m*/d*/y* — matches nextMsgId in JourneyTree.
function maxId(nodes) {
  let max = 0;
  const walk = (ns) => {
    for (const n of ns || []) {
      const m = /^[mdy](\d+)$/.exec(n && n.id);
      if (m) max = Math.max(max, Number(m[1]));
      if (n && n.type === 'decision') for (const b of n.branches || []) walk(b.nodes);
    }
  };
  walk(nodes);
  return max;
}
const blankEmail = (id, subject = '') => ({ id, type: 'message', channel: 'email', delayHours: 0, subject, body: '', ctaText: '', ctaUrl: '', contentMode: 'template' });

// The gates worth offering. `when` is set EXPLICITLY rather than inferred from
// the label, so renaming a branch later can never silently re-point it at a
// different signal.
export const GATES = {
  bought: { key: 'bought', title: 'Did they register / buy?', question: 'Have they registered or bought by now?', yes: 'Registered / bought', no: 'Not yet', wait: 48 },
  clicked: { key: 'clicked', title: 'Did they click?', question: 'Did they click the last email?', yes: 'Clicked', no: 'No click', wait: 24 },
  opened: { key: 'opened', title: 'Did they open?', question: 'Did they open the last email?', yes: 'Opened', no: 'No open', wait: 24 },
};

// ── "After 48 hours…" over a box that says 1 day ──────────────────────────────
// A decision has exactly ONE piece of timing the engine reads: `waitHours`. On
// arrival server/journeys.js opens a window of that length, checks the signals
// every tick, sends anyone who acts down their arm IMMEDIATELY, and fires the
// timeout arm when the window runs out. The QUESTION is free text — a label.
//
// So a question that names a duration is an unchecked claim, and the Owl writes
// them ("After 48 hours, did they open the email?"). Put that above a wait of 1
// day and the wording is 24 hours out — and the wording is what people read
// (asked 2026-08-20). These find the claim so the editor can say the two
// disagree. Nobody's words are rewritten: which one is wrong is the author's call.
const WORD_NUM = { a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7 };
const UNIT_HOURS = { h: 1, hr: 1, hrs: 1, hour: 1, hours: 1, d: 24, day: 24, days: 24, week: 168, weeks: 168 };
// The duration a decision's question CLAIMS, in hours — null when it names none
// (which is the better way to write one, and what the built-in gates do).
const CLAIM_RE = /((?:after|within|in)\s+)(\d{1,3}|a|an|one|two|three|four|five|six|seven)[\s-]*(h|hr|hrs|hour|hours|d|day|days|week|weeks)\b/i;
export function waitClaimHours(question) {
  const m = CLAIM_RE.exec(String(question || ''));
  if (!m) return null;
  const n = /^\d+$/.test(m[2]) ? Number(m[2]) : WORD_NUM[m[2].toLowerCase()];
  const unit = UNIT_HOURS[m[3].toLowerCase()];
  return n && unit ? n * unit : null;
}
// The same clamp the server applies to `waitHours` (server/journeys.js), so the
// editor never reasons about a window the engine would not honour.
const clampWait = (h) => Math.min(720, Math.max(1, Number(h) || 48));
// A duration written the way a person writes one: whole weeks and whole days
// stay weeks and days, anything else is plain hours.
export function sayHours(h) {
  const n = clampWait(h);
  const unit = (v, one) => `${v} ${one}${v === 1 ? '' : 's'}`;
  if (n % 168 === 0) return unit(n / 168, 'week');
  if (n % 24 === 0) return unit(n / 24, 'day');
  return unit(n, 'hour');
}
// The two ways out of a wait/wording mismatch, as patches the caller applies.
// Both are offered because only the author knows which half is the mistake — the
// point is that neither one should need retyping the question by hand.
//   → { waitHours } : believe the WORDING, move the window to match it.
export function waitPatchFromWording(node) {
  const said = waitClaimHours(node && node.question);
  return said == null ? null : { waitHours: clampWait(said) };
}
//   → { question }  : believe the WINDOW, rewrite the duration the wording names.
// Only the duration is touched — the rest of the author's sentence is theirs.
export function wordingPatchFromWait(node) {
  const q = String((node && node.question) || '');
  if (waitClaimHours(q) == null) return null;
  return { question: q.replace(CLAIM_RE, (_, prep) => `${prep}${sayHours(node.waitHours)}`) };
}
// { saidHours, actualHours } when a behaviour decision's wording contradicts the
// window it actually waits; null when they agree, or when the question is silent
// on time, or on a split (which forks instantly and never waits).
export function waitMismatch(node) {
  if (!node || node.type !== 'decision' || node.kind === 'split') return null;
  const said = waitClaimHours(node.question);
  if (said == null) return null;
  const actual = clampWait(node.waitHours);
  return said === actual ? null : { saidHours: said, actualHours: actual };
}

// How many nodes an insert after `afterId` would cost, so the caller can check
// the cap BEFORE offering it: the decision, plus a step for the "yes" arm, plus
// a step for the "no" arm when nothing currently follows.
export function gateCost(nodes, afterId) {
  const trailing = nodesAfter(nodes, afterId);
  if (trailing === null) return 0;
  return trailing.length ? 2 : 3;
}
// The server refuses a decision nested more than this deep (MAX_DEPTH in
// server/journeys.js) — and until it started answering 400, that refusal was
// swallowed into a DELETED journey. The editor has to know the limit too, or the
// ＋ check button keeps offering an edit that cannot be saved.
export const MAX_GATE_DEPTH = 3;
// The server's MAX_NODES. Mirrored rather than imported (that module is CommonJS,
// this one is ESM and ships to the browser) — test/journeyEdit.test.js fails if
// the two ever drift, which is the only thing that makes a hand-copy safe.
export const MAX_NODES = 30;
// How many decisions sit ABOVE `afterId` — i.e. the depth the gate inserted after
// it would land at. -1 = the step isn't in this tree.
export function gateDepth(nodes, afterId) {
  const search = (ns, depth) => {
    if ((ns || []).some((n) => n.id === afterId)) return depth;
    for (const n of ns || []) {
      if (n.type !== 'decision') continue;
      for (const b of n.branches || []) { const hit = search(b.nodes, depth + 1); if (hit >= 0) return hit; }
    }
    return -1;
  };
  return search(nodes, 0);
}
// The steps that follow `afterId` in its own branch. null = not found.
export function nodesAfter(nodes, afterId) {
  const search = (ns) => {
    const i = (ns || []).findIndex((n) => n.id === afterId);
    if (i >= 0) return ns.slice(i + 1);
    for (const n of ns || []) {
      if (n.type !== 'decision') continue;
      for (const b of n.branches || []) { const hit = search(b.nodes); if (hit !== null) return hit; }
    }
    return null;
  };
  return search(nodes);
}

// Insert a decision immediately after `afterId`, moving everything that followed
// it in that branch onto the "didn't" arm. Returns the new node list, or null if
// the step isn't there. Never mutates the input.
export function insertGate(nodes, afterId, gateKey = 'bought') {
  if (nodesAfter(nodes, afterId) === null) return null;
  const g = GATES[gateKey] || GATES.bought;
  let seq = maxId(nodes);
  const nextId = (p) => `${p}${(seq += 1)}`;
  const build = (ns) => {
    const i = ns.findIndex((n) => n.id === afterId);
    if (i < 0) return null;
    const trailing = ns.slice(i + 1);
    const decision = {
      id: nextId('d'),
      type: 'decision',
      kind: 'behaviour',
      question: g.question,
      waitHours: g.wait,
      branches: [
        // The reactive arm needs a step of its own: the engine drops an empty
        // branch and then refuses a decision with fewer than two.
        { label: g.yes, when: g.key, nodes: [blankEmail(nextId('m'))] },
        { label: g.no, when: 'timeout', nodes: trailing.length ? trailing : [blankEmail(nextId('m'))] },
      ],
    };
    return [...ns.slice(0, i + 1), decision];
  };
  const walk = (ns) => {
    const here = build(ns || []);
    if (here) return here;
    return (ns || []).map((n) => (n.type === 'decision'
      ? { ...n, branches: (n.branches || []).map((b) => ({ ...b, nodes: walk(b.nodes) })) }
      : n));
  };
  return walk(nodes);
}

// ─── Insert anywhere, and MOVE ────────────────────────────────────────────────
// The editor could only ever ADD A STEP TO THE END of a branch, and only via the
// ＋ row that sits there. So "put the SMS before the reminder, not after it" was
// not an edit — it was a rebuild, and rebuilding loses the live journey.
//
// A POSITION is { path, index }: `path` is the existing list of {d,b} hops that
// names a container ([] = the trunk), and `index` is the SLOT inside it — slot i
// means "before whatever is at i now", and slot === length means the end. So a
// container of 3 nodes has 4 slots, which is exactly what the drop targets are.
//
// Moving PRESERVES THE NODE'S ID (the same object is re-parented, never rebuilt),
// which matters more than it looks: a person's place in a running journey IS their
// node_id, so re-ordering a live journey never strands anyone. Adding and removing
// already behaved that way; moving now does too.

// Where a node lives: { node, path, index }, or null when it isn't in this tree.
export function findNode(nodes, id) {
  const search = (ns, path) => {
    const i = (ns || []).findIndex((n) => n && n.id === id);
    if (i >= 0) return { node: ns[i], path, index: i };
    for (const n of ns || []) {
      if (!n || n.type !== 'decision') continue;
      for (let b = 0; b < (n.branches || []).length; b += 1) {
        const hit = search(n.branches[b].nodes, [...path, { d: n.id, b }]);
        if (hit) return hit;
      }
    }
    return null;
  };
  return search(nodes, []);
}

// The node list a path names. null = the path doesn't resolve (a decision was
// removed, or a branch index is out of range).
export function containerAt(nodes, path) {
  let cur = nodes || [];
  for (const hop of path || []) {
    const d = cur.find((n) => n && n.id === hop.d && n.type === 'decision');
    if (!d || !d.branches || !d.branches[hop.b]) return null;
    cur = d.branches[hop.b].nodes || [];
  }
  return cur;
}

const samePath = (a = [], b = []) => a.length === b.length && a.every((h, i) => h.d === b[i].d && h.b === b[i].b);

// How many decisions are nested INSIDE this node (0 for a message/sync, 1 for a
// decision whose branches hold no further decisions, and so on).
export function subtreeDepth(node) {
  if (!node || node.type !== 'decision') return 0;
  let deepest = 0;
  for (const b of node.branches || []) for (const n of b.nodes || []) deepest = Math.max(deepest, subtreeDepth(n));
  return 1 + deepest;
}

// Immutably put `node` at a position. null when the position doesn't resolve.
export function insertAt(nodes, pos, node) {
  const { path = [], index = 0 } = pos || {};
  if (containerAt(nodes, path) === null) return null;
  const place = (list) => { const at = Math.max(0, Math.min(index, (list || []).length)); return [...(list || []).slice(0, at), node, ...(list || []).slice(at)]; };
  const walk = (ns, depth) => {
    if (depth === path.length) return place(ns);
    const hop = path[depth];
    return (ns || []).map((n) => (n && n.id === hop.d && n.type === 'decision'
      ? { ...n, branches: n.branches.map((b, i) => (i === hop.b ? { ...b, nodes: walk(b.nodes, depth + 1) } : b)) }
      : n));
  };
  return walk(nodes, 0);
}

// Remove by id with NO validation — the half of a move that must not judge the
// intermediate tree (a branch is briefly empty between the lift and the drop).
// Callers that remove for real want removeNode in JourneyTree.jsx, which does.
function stripNode(nodes, id) {
  return (nodes || []).filter((n) => !n || n.id !== id).map((n) => (n && n.type === 'decision'
    ? { ...n, branches: n.branches.map((b) => ({ ...b, nodes: stripNode(b.nodes, id) })) }
    : n));
}

// The invariants a saved tree has to meet (server/journeys.js validateJourney):
// at least one message, and no decision whose every arm ends the journey. An
// individual empty branch is legal — it means "they converted, send nothing more".
function treeValid(nodes) {
  let messages = 0; let dead = false;
  const walk = (ns) => {
    for (const n of ns || []) {
      if (n.type === 'message') messages += 1;
      if (n.type === 'decision') {
        if (!(n.branches || []).some((b) => (b.nodes || []).length)) dead = true;
        for (const b of n.branches || []) walk(b.nodes);
      }
    }
  };
  walk(nodes);
  return messages > 0 && !dead;
}

// WHY a move can't happen, in words the editor can show — '' when it can.
// Refusing silently is how an editor teaches people it's broken.
export function moveRefusal(nodes, id, pos) {
  const found = findNode(nodes, id);
  if (!found) return 'That step isn’t in this journey any more.';
  const path = (pos && pos.path) || [];
  if (containerAt(nodes, path) === null) return 'That spot isn’t part of the journey any more.';
  // A decision cannot be dropped inside itself — the tree would contain itself,
  // and the engine's compile() would walk it forever.
  if (found.node.type === 'decision' && path.some((h) => h.d === id)) {
    return 'A check can’t be moved inside one of its own branches.';
  }
  // Depth: where it would LAND, plus how deep the thing being moved already is.
  const wouldReach = path.length + subtreeDepth(found.node);
  if (wouldReach > MAX_GATE_DEPTH) {
    return `A journey can only branch ${MAX_GATE_DEPTH} levels deep — moving this here would make it ${wouldReach}. Flatten it, or move it further up the flow.`;
  }
  return '';
}

// Move a node to a position. Returns the new tree, the ORIGINAL tree for a no-op
// (dropping something back where it already is), or null when it can't be done —
// call moveRefusal first if you want to tell the user why.
export function moveNode(nodes, id, pos) {
  const found = findNode(nodes, id);
  if (!found || moveRefusal(nodes, id, pos)) return null;
  const path = (pos && pos.path) || [];
  let index = Math.max(0, Number(pos && pos.index) || 0);
  if (samePath(found.path, path)) {
    // Dropping into its own slot, or the slot immediately after it, changes
    // nothing — say so by returning the tree untouched rather than churning it.
    if (index === found.index || index === found.index + 1) return nodes;
    // Lifting the node first shifts every later slot in ITS OWN container down one.
    if (index > found.index) index -= 1;
  }
  const next = insertAt(stripNode(nodes, id), { path, index }, found.node);
  return next && treeValid(next) ? next : null;
}

// ─── The palette ──────────────────────────────────────────────────────────────
// What can be dropped into a slot. Ids are minted by the caller (nextMsgId in
// JourneyTree.jsx), which is the one place that guarantees a number above the
// tree's current maximum — so a new node never collides with a live person's
// node_id. `cost` is how many nodes it adds, checked against MAX_NODES first.
export const PALETTE = [
  { key: 'email', label: 'Email', prefix: 'm', cost: 1, make: (id) => ({ id, type: 'message', channel: 'email', delayHours: 0, subject: '', body: '', ctaText: '', ctaUrl: '', contentMode: 'template' }) },
  { key: 'sms', label: 'SMS', prefix: 'm', cost: 1, make: (id) => ({ id, type: 'message', channel: 'sms', delayHours: 0, subject: '', body: '', ctaText: '', ctaUrl: '' }) },
  // A WhatsApp step sends an APPROVED template (wording fixed at approval): the
  // step picks the template, its header picture/clip, and the client's own reply
  // to a quick-reply tap. Offered only where the client's WhatsApp flag is on.
  { key: 'whatsapp', label: 'WhatsApp', prefix: 'm', cost: 1, make: (id) => ({ id, type: 'message', channel: 'whatsapp', delayHours: 0, subject: '', body: '', ctaText: '', ctaUrl: '', waTemplateId: '', waFollowUp: '', waOpeners: [], heroImage: '' }) },
  // A WAIT is a message node's delay with nothing to send — but as its own card it
  // is a thing you can see and drag, which "there is a 2 in the delay box of the
  // next email" never was.
  { key: 'wait', label: 'Wait', prefix: 'm', cost: 1, make: (id) => ({ id, type: 'message', channel: 'email', delayHours: 24, subject: '', body: '', ctaText: '', ctaUrl: '', contentMode: 'template', wait: true }) },
  { key: 'sync', label: 'Ad audience', prefix: 'y', cost: 1, make: (id) => ({ id, type: 'sync', platform: 'meta', action: 'add', audienceName: '', note: '' }) },
  // ➡ A HANDOFF hands whoever reaches it to another journey (enrolled on the tick
  // they arrive, honouring that journey's own first-step delay) or to a named
  // list kept in step with this stage. A pass-through, like the ad audience —
  // put it last on a branch and it is the exit.
  { key: 'handoff', label: 'Send to…', prefix: 'h', cost: 1, make: (id) => ({ id, type: 'handoff', target: 'journey', targetActionId: '', segmentName: '', note: '' }) },
  // A SPLIT forks instantly on who someone IS — ticket type, category, gender,
  // age, city. It was authorable only by the Owl until now: the editor rendered
  // one but had no way to make or change one, which is why "VIPs get a different
  // email" meant opening the chat. `depth: 1` is the nesting it adds, so a slot
  // already at the limit refuses it instead of building a tree the server rejects.
  {
    key: 'split',
    label: 'Split by who they are',
    prefix: 'd',
    cost: 2,
    depth: 1,
    make: (id) => ({
      id,
      type: 'decision',
      kind: 'split',
      question: 'Which group are they in?',
      field: 'core_ticket_types.name', // the everyday one; the editor swaps it
      branches: [
        { label: 'This group', values: [], nodes: [blankEmail(`${id}a`)] },
        { label: 'Everyone else', values: null, nodes: [] },
      ],
    }),
  },
];
// How much NESTING a palette item adds (0 for a step, 1 for a split).
const itemDepth = (item) => (item && item.depth) || 0;
// WHY an insert can't happen — '' when it can. Same shape as moveRefusal, so the
// slot can grey one palette entry out rather than accepting a drop that fails.
export function insertRefusal(nodes, pos, item) {
  const path = (pos && pos.path) || [];
  if (containerAt(nodes, path) === null) return 'That spot isn’t part of the journey any more.';
  const wouldReach = path.length + itemDepth(item);
  if (wouldReach > MAX_GATE_DEPTH) return `A journey can only branch ${MAX_GATE_DEPTH} levels deep — a split can’t go this far in.`;
  return '';
}
export const paletteItem = (key) => PALETTE.find((p) => p.key === key) || null;
